import { Box, Grid, Typography, Paper, IconButton, useTheme, Divider, Switch, FormControlLabel } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FiDownload, FiX, FiLayers } from "react-icons/fi";
import { LuFactory, LuTruck, LuZap, LuFootprints, LuBox, LuActivity, LuInfo } from "react-icons/lu";

import { MainContainer } from "../../components/container/MainContainer";
import { lca_phase_map } from "../../components/LCA_Phases";
import { Product } from "../../db_access/product";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";

import MetricCard from "../../app_components/MetricCard";
import LifecycleStageCards from "../../app_components/LifecycleStageCards";
import AgGrid from "../../components/grid/AgGrid";
import {
  centerAlignColDef,
  defaultColDef,
  floatFormater,
  leftAlignColDef,
  rightAlignColDef,
} from "../../components/grid/columnDefinitions/columnDefinitions";
import HorizontalStackedBarChart from "../../components/chart/HorizontalStackedBarChart";
import { GRAPH_PALETTE, getGraphColorByStage } from "../../theme/style/graphChartPalette";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { envfactor_types } from "../../components/Envfactor_Types";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";

import "../../style/pages-style/Comparison.css";


function ProductComparison() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [comparisonBasedOn, setComparisonBasedOn] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [comparisonBasedOnOptions, setComparisonBasedOnOptions] = useState([]);
  const [rawComparisonData, setRawComparisonData] = useState([]);
  const [showBenchmark, setShowBenchmark] = useState(true);
  const [baselineEnvTypeData, setBaselineEnvTypeData] = useState([]);
  const [baselineStageData, setBaselineStageData] = useState({});

  useEffect(() => {
    Product.get_mine()
      .then((response) => response.json())
      .then((data) => {
        setComparisonBasedOnOptions(data.map((i) => ({ value: i.id, label: i.name || i.id })).sort((a, b) => a.label.localeCompare(b.label)));
      });
  }, []);

  const handleApply = () => {
    const allSelectedValues = Array.from(new Set([comparisonBasedOn, ...selectedValues])).filter(Boolean);

    if (allSelectedValues.length > 0) {
      Product.get_comparison_gwp_by_stage(allSelectedValues)
        .then((response) => response.json())
        .then((data) => {
          const uniqueData = Array.from(new Map(data.map(item => [item.product_id, item])).values());
          setRawComparisonData(uniqueData);

          // Update baseline stage data if baseline changed or data fetched
          const baselineData = uniqueData.find(d => String(d.product_id) === String(comparisonBasedOn));
          if (baselineData) {
            setBaselineStageData(baselineData);
          }
        })
        .catch(err => console.error("Error fetching comparison data:", err));
    } else {
      setRawComparisonData([]);
    }

    // Fetch baseline env type data specifically for the metric cards
    if (comparisonBasedOn) {
      Product.get_total_gwp_by_env_type([comparisonBasedOn])
        .then(res => res.json())
        .then(data => setBaselineEnvTypeData(data))
        .catch(err => console.error("Error fetching baseline env type data:", err));
    } else {
      setBaselineEnvTypeData([]);
      setBaselineStageData({});
    }
  };

  useEffect(() => {
    handleApply();
  }, [comparisonBasedOn, selectedValues]);

  const formattedData = useMemo(() => {
    return rawComparisonData.map((d) => {
      const productOption = comparisonBasedOnOptions.find((option) => String(option.value) === String(d.product_id));
      const productName = productOption ? productOption.label : d.product_id;
      return {
        id: d.product_id,
        name: productName,
        productPhase: parseFloat(d[lca_phase_map["Product Phase"]] || 0),
        constructionPhase: parseFloat(d[lca_phase_map["Construction Phase"]] || 0),
        usePhase: parseFloat(d[lca_phase_map["Use Phase"]] || 0),
        endOfLifePhase: parseFloat(d[lca_phase_map["End-of-life Phase"]] || 0),
        benefitsPhase: parseFloat(d[lca_phase_map["Benefits Phase"]] || 0),
        total: parseFloat(d?.["total"] || 0),
      };
    });
  }, [rawComparisonData, comparisonBasedOnOptions]);

  const selectedItemData = useMemo(() => {
    return formattedData.find(d => d.id === comparisonBasedOn) || {
      productPhase: 0, constructionPhase: 0, usePhase: 0, endOfLifePhase: 0, benefitsPhase: 0, total: 0, name: "Baseline"
    };
  }, [formattedData, comparisonBasedOn]);

  const averages = useMemo(() => {
    if (formattedData.length === 0) return { productPhase: 0, constructionPhase: 0, usePhase: 0, endOfLifePhase: 0, benefitsPhase: 0, total: 0, material: 0, transport: 0, energy: 0, a1a3: 0 };
    const count = formattedData.length;
    
    // We need to calculate averages for the higher level metrics too
    const gwpByEnvType = rawComparisonData.reduce((acc, d) => {
      // This is a simplified average calculation for Material/Transport/Energy/A1-A3
      // In a real scenario, we might need more granular data from a different endpoint
      // mapping stages to categories if not already provided
      return acc;
    }, {});

    return {
      productPhase: (formattedData.reduce((acc, curr) => acc + curr.productPhase, 0) / count),
      constructionPhase: (formattedData.reduce((acc, curr) => acc + curr.constructionPhase, 0) / count),
      usePhase: (formattedData.reduce((acc, curr) => acc + curr.usePhase, 0) / count),
      endOfLifePhase: (formattedData.reduce((acc, curr) => acc + curr.endOfLifePhase, 0) / count),
      benefitsPhase: (formattedData.reduce((acc, curr) => acc + curr.benefitsPhase, 0) / count),
      total: (formattedData.reduce((acc, curr) => acc + curr.total, 0) / count),
      // For simplified comparison if granular per-product env_type is not available in rawComparisonData:
      material: (formattedData.reduce((acc, curr) => acc + (curr.productPhase * 0.8), 0) / count), // placeholder ratio
      transport: (formattedData.reduce((acc, curr) => acc + curr.constructionPhase, 0) / count),
      energy: (formattedData.reduce((acc, curr) => acc + curr.usePhase, 0) / count),
      a1a3: (formattedData.reduce((acc, curr) => acc + curr.productPhase, 0) / count),
    };
  }, [formattedData, rawComparisonData]);

  const baselineMetrics = useMemo(() => {
    const temp = baselineEnvTypeData.reduce(
      (acc, r) => {
        const total = Number(r.gwp_sum) || 0;
        const transport = envfactor_types.transport === r.type ? total : 0;
        const energy = envfactor_types.energy_sources === r.type ? total : 0;
        const material = total - transport - energy;
        acc.total += total;
        acc.transport += transport;
        acc.energy += energy;
        acc.material += material;
        return acc;
      },
      { total: 0, transport: 0, energy: 0, material: 0 }
    );
    return temp;
  }, [baselineEnvTypeData]);

  const baselineStages = useMemo(() => {
    const baselineProd = comparisonBasedOnOptions.find(o => o.value === comparisonBasedOn);
    // Note: We'd need extra product data (mci, reused_gwp) for full consistency,
    // assuming here we only have A1-A3 from the stage API for now
    return {
      a1a3: Number(baselineStageData?.A1_A3 || 0),
      // reused: 0, 
      // mci: 0
    };
  }, [baselineStageData, comparisonBasedOn, comparisonBasedOnOptions]);

  const gridData = useMemo(() => {
    return formattedData.map(d => ({
      id: d.id,
      name: d.name,
      product_phase: d.productPhase,
      construction_phase: d.constructionPhase,
      use_phase: d.usePhase,
      eol_phase: d.endOfLifePhase,
      benefits_phase: d.benefitsPhase,
      total: d.total
    }));
  }, [formattedData]);

  const colDefs = useMemo(() => [
    { headerName: "Name", field: "name", ...leftAlignColDef, minWidth: 200, flex: 1, cellStyle: { color: "#659d33", fontWeight: 600 } },
    { headerName: "A1 - A3", field: "product_phase", ...rightAlignColDef, ...floatFormater },
    { headerName: "Total GWP kg CO₂e", field: "total", ...defaultColDef, ...rightAlignColDef, ...floatFormater, cellStyle: { ...rightAlignColDef.cellStyle, fontWeight: 700 } },
  ], []);

  const chartStackData = useMemo(() => {
    return formattedData.map(d => ({
      label: d.name,
      "A1 - A3": d.productPhase,
    }));
  }, [formattedData]);

  const comparisonChartData = useMemo(() => [
    { label: t("Total GWP"), Baseline: selectedItemData.total, Average: averages.total || 0 },
    { label: t("A1 - A3"), Baseline: baselineStages.a1a3, Average: averages.a1a3 || 0 },
  ], [selectedItemData, averages, baselineStages, t]);

  return (
    <MainContainer>
      {/* METRIC CARDS SECTION */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1.5fr" }, gap: 2.5 }}>
          <Box>
            <MetricCard
              title={t("Baseline GWP")}
              value={baselineMetrics.total || 0}
              icon={LuFootprints}
              accent="#16a34a"
              bg="rgba(22, 163, 74, 0.12)"
              unit="kg CO₂e"
              delay={0.05}
            />
          </Box>
          <Box>
            <MetricCard
              title={t("A1 - A3 (Product Phase)")}
              value={baselineStages.a1a3 || 0}
              icon={LuFactory}
              accent="#0891b2"
              bg="rgba(8, 145, 178, 0.12)"
              unit="kg CO₂e"
              delay={0.15}
            />
          </Box>
        </Box>
      </Box>

      {/* SELECTION SECTION */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: "Poppins", fontSize: "20px", fontWeight: 500, color: isDark ? "#fff" : "#0f172a", mb: 2, textAlign: 'left' }}>
          {t("Comparison Product Selection")}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, p: 3, borderRadius: "12px", bgcolor: tok.bg.card, border: "1px solid", borderColor: tok.border.card, boxShadow: tok.shadow.card, position: 'relative', zIndex: 10 }}>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1, fontWeight: 600, color: isDark ? "#a1a1aa" : "text.secondary", ml: 0.5 }}>
              {t("Baseline Product")}
            </Typography>
            <Select
              options={comparisonBasedOnOptions}
              value={comparisonBasedOnOptions.find(o => o.value === comparisonBasedOn)}
              onChange={(opt) => setComparisonBasedOn(opt ? opt.value : "")}
              placeholder="Select baseline..."
              isClearable
              menuPortalTarget={document.body}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderRadius: "8px",
                  border: state.isFocused ? `1px solid #659d33` : `1px solid ${tok.border.card}`,
                  minHeight: "44px",
                  backgroundColor: isDark ? "#18181b" : "white",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#659d33" }
                }),
                menu: (base) => ({ ...base, backgroundColor: isDark ? "#18181b" : "white", zIndex: 9999 }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? "#659d33" : state.isFocused ? (isDark ? "rgba(101, 157, 51, 0.15)" : "rgba(101, 157, 51, 0.08)") : "transparent",
                  color: state.isSelected ? "white" : (isDark ? "white" : "black"),
                  "&:active": { backgroundColor: "#659d33" }
                }),
                singleValue: (base) => ({ ...base, color: isDark ? "white" : "black" }),
                input: (base) => ({ ...base, color: isDark ? "white" : "black" })
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1, fontWeight: 600, color: isDark ? "#a1a1aa" : "text.secondary", ml: 0.5 }}>
              {t("Comparison Products")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", minHeight: "44px" }}>
              {selectedValues.map((val) => {
                const opt = comparisonBasedOnOptions.find(o => o.value === val);
                if (!opt) return null;
                return (
                  <Box
                    key={val}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pl: 2,
                      pr: 1.5,
                      py: 0.8,
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: tok.border.card,
                      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "white",
                      fontSize: "0.85rem",
                      color: tok.text.primary,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#659d33",
                        boxShadow: "0 2px 8px rgba(101,157,51,0.15)"
                      }
                    }}
                  >
                    {opt.label}
                    <Box
                      component="span"
                      sx={{ cursor: "pointer", display: "flex", color: "text.secondary", transition: "color 0.2s", "&:hover": { color: "#ef4444" } }}
                      onClick={() => setSelectedValues(prev => prev.filter(v => v !== val))}
                    >
                      <FiX size={14} />
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ width: selectedValues.length > 0 ? "180px" : "100%" }}>
                <Select
                  options={comparisonBasedOnOptions.filter(o => o.value !== comparisonBasedOn && !selectedValues.includes(o.value))}
                  onChange={(opt) => opt && setSelectedValues(prev => [...prev, opt.value])}
                  placeholder={selectedValues.length > 0 ? "Add more..." : "Search and add products to compare..."}
                  value={null}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      border: state.isFocused ? `1px solid #659d33` : `1px solid ${tok.border.card}`,
                      minHeight: "44px",
                      backgroundColor: selectedValues.length > 0 ? (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)") : (isDark ? "#18181b" : "white"),
                      fontSize: "0.85rem",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#659d33" }
                    }),
                    menu: (base) => ({ ...base, backgroundColor: isDark ? "#18181b" : "white", zIndex: 9999 }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? "#659d33" : state.isFocused ? (isDark ? "rgba(101, 157, 51, 0.15)" : "rgba(101, 157, 51, 0.08)") : "transparent",
                      color: state.isSelected ? "white" : (isDark ? "white" : "black"),
                      "&:active": { backgroundColor: "#659d33" }
                    }),
                    input: (base) => ({ ...base, color: isDark ? "white" : "black" }),
                    placeholder: (base) => ({ ...base, color: isDark ? "#a1a1aa" : "#64748b" })
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Paper variant="Table Container" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: tok.text.chartTitle, fontFamily: GRAPH_PALETTE.font.family }}>{t("Footprint Summary")}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.75rem", fontStyle: "italic", verticalAlign: 'middle', display: 'flex', alignItems: 'center', pt: 0.5 }}>
              ({t("All units are calculated in kg CO₂e")})
            </Typography>
          </Box>
          <IconButton size="small"><FiDownload size={18} /></IconButton>
        </Box>
        <Box sx={{ borderBottom: `1px solid ${tok.border.divider}` }}>
          <AgGrid
            rowData={gridData}
            columnDefs={colDefs}
            domLayout={gridData.length > 20 ? "normal" : "autoHeight"}
            pagination={gridData.length > 20}
          />
        </Box>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: showBenchmark ? { xs: "1fr", lg: "1fr 1fr" } : "1fr", gap: 4, mb: 4 }}>
        {/* PHASE BREAKDOWN CHART */}
        <Paper elevation={0} sx={{ p: 1, borderRadius: tok.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
              {t("Impact Distribution by Product")}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showBenchmark}
                    onChange={(e) => setShowBenchmark(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#659d33' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#659d33' },
                    }}
                  />
                }
                label={t("Benchmark Comparison")}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    fontWeight: 500
                  }
                }}
              />
              <IconButton size="small"><FiDownload size={18} /></IconButton>
            </Box>
          </Box>
          <HorizontalStackedBarChart
            data={chartStackData}
            dataKeys={["A1 - A3"]}
            coloredBars={true}
            showLegend={false}
            height={Math.max(450, chartStackData.length * 60 + 100)}
            xAxisLabel="GWP Impact (kg CO₂e)"
            yAxisLabel=""
            fillHeight={showBenchmark}
          />
        </Paper>

        {/* COMPARATIVE METRIC ANALYSIS (IMPROVISED) */}
        {showBenchmark && (
          <Paper elevation={0} sx={{ p: 1, borderRadius: tok.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, overflow: "hidden", animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, px: 3, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("Metric Benchmark Comparison")}
              </Typography>
              <IconButton size="small"><FiDownload size={18} /></IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 450, p: 3, "& .recharts-cartesian-grid-horizontal line": { strokeOpacity: isDark ? 0.1 : 0.05 }, animation: "org-fadeIn 0.3s ease" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparisonChartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="label" 
                  axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
                  tickLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick }}
                  tick={{ 
                    fill: tok.text.chartTitle, 
                    fontSize: GRAPH_PALETTE.font.sizes.standard, 
                    fontFamily: GRAPH_PALETTE.font.family,
                    fontWeight: GRAPH_PALETTE.font.weights.standard 
                  }} 
                  dy={10}
                />
                <YAxis 
                  axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
                  tickLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.tick : GRAPH_PALETTE.axes.lightMode.tick }}
                  tick={{ 
                    fill: tok.text.chartTitle, 
                    fontSize: GRAPH_PALETTE.font.sizes.standard, 
                    fontFamily: GRAPH_PALETTE.font.family 
                  }}
                  label={{ 
                    value: t('kg CO₂e'), 
                    angle: -90, 
                    position: 'insideLeft', 
                    style: { 
                      textAnchor: 'middle', 
                      fill: tok.text.chartTitle, 
                      opacity: 0.8, 
                      fontSize: GRAPH_PALETTE.font.sizes.mainAxisTitle, 
                      fontFamily: GRAPH_PALETTE.font.family,
                      fontWeight: GRAPH_PALETTE.font.weights.bold 
                    } 
                  }}
                />
                <Tooltip 
                  cursor={{ fill: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
                  contentStyle={{ 
                    backgroundColor: tok.bg.card, 
                    border: `1px solid ${tok.border.card}`, 
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    fontFamily: GRAPH_PALETTE.font.family,
                    fontSize: GRAPH_PALETTE.font.sizes.standard
                  }}
                  itemStyle={{ fontSize: GRAPH_PALETTE.font.sizes.standard }}
                  formatter={(value, name) => [Number(value).toFixed(1), name]}
                />
                <Legend 
                  verticalAlign="top" 
                  align="right" 
                  iconType="circle"
                  wrapperStyle={{ 
                    paddingBottom: 30, 
                    fontFamily: GRAPH_PALETTE.font.family, 
                    fontSize: GRAPH_PALETTE.font.sizes.standard,
                    fontWeight: GRAPH_PALETTE.font.weights.medium 
                  }}
                />
                <Bar 
                  dataKey="Baseline" 
                  name={t("Baseline Product")} 
                  fill={GRAPH_PALETTE.lcaStages.A1} 
                  radius={[4, 4, 0, 0]} 
                  barSize={32}
                />
                <Bar 
                  dataKey="Average" 
                  name={t("Selection Average")} 
                  fill={GRAPH_PALETTE.lcaStages.A3} 
                  radius={[4, 4, 0, 0]} 
                  barSize={32}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
        )}
      </Box>
    </MainContainer>
  );
}

export default ProductComparison;

